/* Theme state. The *initial* apply happens pre-paint in the inline script in
   app/templates/index.html (an ES module runs too late and would flash); this
   module owns every change after that and must stay compatible with it:
   same localStorage keys, same data-theme attribute, same theme-color patch.

   Theme now has two controls — the header's cycle button and Settings'
   segmented row — because removing the header on mobile would otherwise leave
   it unreachable. Two views of one value, so the value lives here and both
   subscribe; neither holds its own copy to drift. */

import type { ThemePref } from './types.js';

const KEY = 'grain-theme';
/**
 * What the installed PWA paints its status bar with — `--bg`, not the accent.
 *
 * Android hands a standalone app a viewport that *starts below* the status bar
 * (measured on the reference device: `--safe-t` is 0px even in standalone,
 * because it has no cutout), so there is no bleeding underneath it and this band
 * is the only control we have over that strip. Filled with the accent it read as
 * a marigold header bar the app does not otherwise have — most obviously over
 * Map, which is edge-to-edge below it. `--bg` makes it continuous with the app.
 *
 * Mirrored by the pre-paint script in app/templates/index.html; keep the two
 * sets identical or the status bar changes colour on first paint.
 */
const BAR = { light: '#f4efe4', dark: '#17140f' } as const;

const media = window.matchMedia('(prefers-color-scheme: dark)');

export function getPref(): ThemePref {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

export function resolve(pref: ThemePref = getPref()): 'light' | 'dark' {
  if (pref === 'light' || pref === 'dark') return pref;
  return media.matches ? 'dark' : 'light';
}

function apply(pref: ThemePref): void {
  const r = resolve(pref);
  document.documentElement.dataset.theme = r;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR[r]);
}

const listeners = new Set<() => void>();

/**
 * Be told when the preference changes, from wherever it was changed.
 *
 * Returns an unsubscribe fn. Deliberately parameterless: a subscriber re-reads
 * `getPref()`, so there is no second copy of the value to fall out of date.
 */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setPref(pref: ThemePref): void {
  localStorage.setItem(KEY, pref);
  apply(pref);
  for (const fn of listeners) fn();
}

/** Cycle order for the header's single toggle button. */
export function cycle(): ThemePref {
  const next: Record<ThemePref, ThemePref> = { auto: 'light', light: 'dark', dark: 'auto' };
  const pref = next[getPref()];
  setPref(pref);
  return pref;
}

/** Keep 'auto' tracking the OS. Returns an unsubscribe fn. */
export function watchSystem(onChange: () => void): () => void {
  const handler = () => {
    if (getPref() === 'auto') {
      apply('auto');
      onChange();
    }
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
